import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { DEFAULT_DATE_RANGE } from '../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { runLoading } from '../../components/loading';
import { useNavActions } from '../../hooks/useNavActions';
import { apiMessage, downloadEddTemplate, resendNotification } from './suboutRegistration/api';

export interface SuboutOrderRow {
  id:                   string;             // composite "{SuboutOid}:{JobID}"
  orderId:              string;
  jobID:                string | null;      // singular per row (matches GoLive)
  division:             string | null;      // the job's Division (Company) — Test Order only
  dateRegistered:       string | null;
  dateShipped:          string | null;
  dueDate:              string | null;
  dateReceived:         string | null;
  dateDelivered:        string | null;
  trackingNo:           string | null;
  numberOfBottles:      number | null;
  po:                   string | null;
  poId:                 string | null;
  total:                string | null;
  reason:               string | null;
  statusCode:           number;
  statusText:           string;
  statusId:             string | null;      // "SS-1-3" etc.
  notifyCode:           number;
  notifyText:           string;
  mailDateSent:         string | null;
  lastUpdatedDate:      string | null;
  contractLabName:      string | null;
  labAddress:           string | null;      // street address
  contractLabLocation:  string | null;      // City + State (back-compat)
  contractLabContact:   string | null;
  contractLabPhone:     string | null;
  turnAroundTime:       string | null;      // pre-formatted: "25 Days"
  deliveryService:      string | null;      // SuboutDeliveryService.DeliveryServiceName
  testForSubout:        string | null;
  jobIDs:               string | null;      // back-compat (same as jobID now)
  client:               string | null;
  sampleCount:          number;
  sendBy:               string | null;
}

type ViewKind = 'test-orders' | 'notifications';
// Subout supports the same range buttons as everything else (2Y/5Y just hidden
// in the dropdown for visual compactness).  DateRangeKey kept locally so the
// rest of the file's existing logic still type-checks against its own union.
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | 'All';

interface SuboutListPageProps {
  view:      ViewKind;
  title:     string;
  subtitle:  string;
}

// Subout's dropdown trims 2Y/5Y because the SP itself only ever scans 1Y back.

// Shared §14b.6 helpers (local tz, 24-hour, no AM/PM, force-UTC inside); keep
// the '—' placeholder for empty values.
function fmtDate(v?: string | null): string {
  return fmtLocalDate(v) || '—';
}
function fmtDateTime(v?: string | null): string {
  return fmtLocalDateTime(v) || '—';
}
function sinceFromKey(k: DateRangeKey): string {
  if (k === 'All') return '2020-01-01';
  const now = new Date();
  const map: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()),
    '3M': () => new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    '6M': () => new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
    '1Y': () => new Date(now.getFullYear() - 1, now.getMonth(),  now.getDate()),
  };
  const d = map[k as Exclude<DateRangeKey, 'All'>]();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Subout uses the shared statusVariant() helper (via AppDataGrid's type:'status'
// column) — same mapping every list page uses (was per-page before; gave
// inconsistent colours across forms).

type StatusChip = 'all' | string;

export default function SuboutListPage({ view, title, subtitle }: SuboutListPageProps) {
  const notify = useNotify();
  const confirm = useConfirm();
  // Notification Queue → Resend needs Write on the queue view (G5 role flags).
  const notifyPerm = useNavActions('suboutnotif');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [rows, setRows]     = useState<SuboutOrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError]   = useState<string | null>(null);

  const [dateRange, setDateRange] = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SuboutTracking as DateRangeKey);
  const [search,    setSearch]    = useState('');
  const [statusChip, setStatusChip] = useState<StatusChip>('all');

  const endpoint = view === 'test-orders'
    ? '/api/v1/subout/test-orders'
    : '/api/v1/subout/notifications';
  const isNotify = view === 'notifications';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get(endpoint, { params: { sinceDate: sinceFromKey(dateRange) } });
      setRows((res.data as { data: SuboutOrderRow[] }).data ?? []);
    } catch (e) {
      console.error(e); setRows([]);
      setError('Failed to load — check the API logs.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [endpoint, dateRange]);

  useEffect(() => { void load(); }, [load]);

  // Search + status filter.  AppDataGrid does per-column filter + sort +
  // pagination; this page keeps only the global search + the status chips.
  const searchFiltered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r =>
      (r.orderId         ?? '').toLowerCase().includes(term) ||
      (r.contractLabName ?? '').toLowerCase().includes(term) ||
      (r.client          ?? '').toLowerCase().includes(term) ||
      (r.jobIDs          ?? '').toLowerCase().includes(term) ||
      (r.testForSubout   ?? '').toLowerCase().includes(term) ||
      (r.trackingNo      ?? '').toLowerCase().includes(term));
  }, [rows, search]);

  const filtered = useMemo(() => {
    if (statusChip === 'all') return searchFiltered;
    return isNotify
      ? searchFiltered.filter(x => x.notifyText === statusChip)
      : searchFiltered.filter(x => x.statusText === statusChip);
  }, [searchFiltered, statusChip, isNotify]);

  // Chip definitions from the data we have
  const chips = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of searchFiltered) {
      const k = isNotify ? r.notifyText : r.statusText;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [searchFiltered, isNotify]);

  // ── Notification Queue actions (G5 ResendMail_Execute :9037 / EDDTemplate :8856) ──
  const orderOidOf = (r: SuboutOrderRow) => r.id.split(':')[0] ?? r.id;

  const resend = useCallback(async (r: SuboutOrderRow) => {
    if (!notifyPerm.write) { notify.warning('You do not have permission to resend subout emails.'); return; }
    const ok = await confirm.confirm({
      title: 'Resend',
      message: `Send the notification email for subout order ${r.orderId} to the contract lab again?`,
    });
    if (!ok) return;
    try {
      const out = await resendNotification(orderOidOf(r));
      notify.success(out.message ?? 'Your email has been sent successfully.');
      void load();
    } catch (e) {
      notify.error(apiMessage(e, 'The email could not be sent.'));
      void load();
    }
  }, [confirm, notify, notifyPerm.write, load]);

  async function eddTemplate() {
    const picked = filtered.filter((r) => selected.has(r.id));
    const row = picked[0];
    if (picked.length !== 1 || !row) { notify.warning('Please select one order to download its EDD template.'); return; }
    try {
      const { blob, fileName } = await runLoading(downloadEddTemplate(orderOidOf(row)),
        { blocking: true, label: 'Building the EDD template…' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      notify.success(`Exported to ${fileName} successfully.`);
    } catch (e) {
      notify.error(apiMessage(e, 'The EDD template could not be built.'));
    }
  }

  // ── Per-view shared-grid columns ──────────────────────────────────────────
  // Column layouts + labels match GoLive ALPACA LIMS exactly (audited
  // 2026-05-24).  Status columns use type:'status' (shared statusPill); JobID +
  // Tracking# keep their identifier look via the shared grid's default black
  // cell.  Date columns render through the page's existing fmt* helpers so the
  // on-screen text is byte-for-byte the same as before the migration.
  const cols = useMemo<AppColumn<SuboutOrderRow>[]>(() => {
    if (isNotify) {
      return [
        {
          // G5 pins a "Resend" column on the queue grid (Ctl :3440).
          field: 'Resend', header: '', width: 90, align: 'center', noExport: true, sortable: false, filterable: false,
          render: r => (
            <button type="button" className="ca-btn" title={`Resend the email for ${r.orderId}`}
                    disabled={!notifyPerm.write}
                    onClick={(ev) => { ev.stopPropagation(); void resend(r); }}>
              <i className="ti ti-send" aria-hidden="true" /> Resend
            </button>
          ),
        },
        { field: 'SuboutOrderID',      header: 'SuboutOrderID', width: 140, text: r => r.orderId, render: r => r.orderId || '—' },
        { field: 'Test',               header: 'Test',          width: 200, text: r => r.testForSubout ?? '', render: r => r.testForSubout || '—' },
        { field: 'DueDate',            header: 'DueDate',       width: 110, text: r => fmtDate(r.dueDate), render: r => fmtDate(r.dueDate) },
        { field: 'ContractLab',        header: 'ContractLab',   width: 200, text: r => r.contractLabName ?? '', render: r => r.contractLabName || '—' },
        { field: 'ContractLabAddress', header: 'ContractLabAddress', width: 200, text: r => r.labAddress ?? r.contractLabLocation ?? '', render: r => (r.labAddress ?? r.contractLabLocation) || '—' },
        { field: 'TrackingNum',        header: 'Tracking#',     width: 130, text: r => r.trackingNo ?? '', render: r => r.trackingNo || '—' },
        { field: 'DeliveryService',    header: 'DeliveryService', width: 130, text: r => r.deliveryService ?? '', render: r => r.deliveryService || '—' },
        { field: 'DateSent',           header: 'DateSent',      width: 140, text: r => fmtDateTime(r.mailDateSent), render: r => fmtDateTime(r.mailDateSent) },
        { field: 'SendBy',             header: 'SendBy',        width: 120, text: r => r.sendBy ?? '—', render: r => r.sendBy ?? '—' },
        { field: 'NotifyStatus',       header: 'Status',        width: 140, type: 'status', text: r => r.notifyText },
      ];
    }
    return [
      { field: 'JobID',          header: 'JobID',          width: 120, text: r => r.jobID ?? r.jobIDs ?? '', render: r => (r.jobID ?? r.jobIDs) || '—' },
      { field: 'SuboutOrderID',  header: 'SuboutOrderID',  width: 140, text: r => r.orderId, render: r => r.orderId || '—' },
      { field: 'DateShipped',    header: 'DateShipped',    width: 110, text: r => fmtDate(r.dateShipped), render: r => fmtDate(r.dateShipped) },
      { field: 'TrackingNum',    header: 'Tracking#',      width: 130, text: r => r.trackingNo ?? '', render: r => r.trackingNo || '—' },
      { field: 'NumSamples',     header: '#Samples',       width:  80, type: 'number', text: r => String(r.sampleCount ?? 0), value: r => r.sampleCount ?? 0 },
      { field: 'ContractLab',    header: 'ContractLab',    width: 200, text: r => r.contractLabName ?? '', render: r => r.contractLabName || '—' },
      { field: 'LabAddress',     header: 'LabAddress',     width: 200, text: r => r.labAddress ?? r.contractLabLocation ?? '', render: r => (r.labAddress ?? r.contractLabLocation) || '—' },
      { field: 'TurnAroundTime', header: 'TurnAroundTime', width: 110, text: r => r.turnAroundTime ?? '', render: r => r.turnAroundTime || '—' },
      { field: 'DueDate',        header: 'DueDate',        width: 110, text: r => fmtDate(r.dueDate), render: r => fmtDate(r.dueDate) },
      { field: 'Status',         header: 'Status',         width: 200, type: 'status', text: r => r.statusText },
      { field: 'StatusID',       header: 'StatusID',       width:  80, text: r => r.statusId ?? '', render: r => r.statusId || '—' },
    ];
  }, [isNotify, notifyPerm.write, resend]);

  return (
    <div className="bl-page">
      {/* ── Page header (breadcrumb / title / subtitle) ── */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Sample SubContract Tracking</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{title}</span>
          </div>
          {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
              via navMeta + the TOP load-time chip. */}
          <div className="dp-title-row">
            <FormTitle pageId={isNotify ? 'suboutnotif' : 'subouttest'} title={title}
              loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            Read-only · {loading ? '…' : rows.length} items
            {subtitle ? <> · {subtitle}</> : null}
          </div>
        </div>
      </div>

      {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
          only the grid card below is white. ── */}
      <div className="bl-grid-area">
        {/* Toolbar on its OWN full-width row — canonical order: [left] · Refresh ·
            Export to.  This read grid has no New/Edit/Delete; the date range
            picker rides in the left slot. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
                {isNotify && (
                  <button type="button" className="ca-btn" onClick={() => void eddTemplate()} disabled={loading}
                          title="Download the selected order's EDD template">
                    <i className="ti ti-file-spreadsheet" aria-hidden="true" /> EDD Template
                  </button>
                )}
              </>
            }
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: view, ...gridExportMatrix(filtered, cols) }}
          />
        </div>

        {/* Search — shared .list-search-row on the grey page, never inside the card. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search order ID, lab, client, job, test…" />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          {filtered.length !== searchFiltered.length && (
            <span className="search-match-count">
              {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>

        {/* ONE white grid card — filter-chip strip + grid + its single footer. */}
        <div className="bl-card bl-card-flex">

          {/* Status filter chips */}
          {chips.length > 0 && (
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">{isNotify ? 'Notification:' : 'Status:'}</span>
              <button
                className={`sa-filter-btn${statusChip === 'all' ? ' active' : ''}`}
                onClick={() => setStatusChip('all')}
              >All ({searchFiltered.length})</button>
              {chips.map(([label, n]) => (
                <button key={label}
                  className={`sa-filter-btn${statusChip === label ? ' active' : ''}`}
                  onClick={() => setStatusChip(label)}
                >{label} ({n})</button>
              ))}
            </div>
          )}

          {/* Grid */}
          {error ? (
            <div className="bl-empty" style={{ color: 'var(--ai-red)', flexDirection: 'column', gap: 6 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: .35 }} />
              <div>{error}</div>
            </div>
          ) : (
            /* Shared data grid — styling, per-column filters, sort, footer /
               pagination and load-time are all standardised in <AppDataGrid>. */
            <AppDataGrid<SuboutOrderRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.id}
              loading={loading}
              loadMs={loadMs}
              selectable={isNotify}
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText={isNotify
                ? 'No notifications waiting. All caught up!'
                : 'No open subout test orders for this date range.'}
            />
          )}
        </div>
      </div>
    </div>
  );
}
